import React, { useState } from 'react';
import { FaSun, FaMapMarkerAlt, FaSearch, FaMoon, FaCamera, FaClock, FaCompass, FaInfoCircle, FaCalendarAlt } from 'react-icons/fa';
import { calculateGoldenHour, geocodeLocation } from '../../api/client';

const GoldenHourPlanner = () => {
  const [loading, setLoading] = useState(false);
  const [lightingData, setLightingData] = useState(null);
  const [error, setError] = useState('');

  // Search inputs
  const [searchCity, setSearchCity] = useState('');
  const [searchState, setSearchState] = useState('');
  const [searchCountry, setSearchCountry] = useState('');
  const [locationName, setLocationName] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const handleSearch = async () => {
    if (!searchCity.trim()) {
      setError('Please enter a city or location');
      return;
    }

    setLoading(true);
    setError('');
    setLightingData(null);

    try {
      // First geocode the location
      const geoData = await geocodeLocation(searchCity.trim(), searchState.trim() || null, searchCountry.trim() || null);

      if (!geoData.results || geoData.results.length === 0) {
        setError('Location not found. Try a different search term.');
        setLoading(false);
        return;
      }

      const location = geoData.results[0];
      const name = locationName.trim() || `${searchCity}${searchState ? ', ' + searchState : ''}`;

      // Get golden hour data
      const data = await calculateGoldenHour(location.lat, location.lng, name, selectedDate || null);
      setLightingData(data);
    } catch (err) {
      console.error('Error calculating golden hour:', err);
      setError('Failed to calculate lighting times. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 rounded-3xl p-8 text-white relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Sun rays animation */}
          <div className="absolute -right-10 top-1/2 transform -translate-y-1/2">
            <div className="w-40 h-40 bg-yellow-300/30 rounded-full blur-3xl animate-pulse" />
          </div>
          <div className="absolute -right-5 top-1/2 transform -translate-y-1/2">
            <div className="w-24 h-24 bg-yellow-200/40 rounded-full blur-2xl" />
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <FaSun className="text-2xl text-yellow-200" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Photography Golden Hour Planner</h2>
              <p className="text-white/70">Perfect lighting times for your location</p>
            </div>
          </div>

          <p className="text-white/80 mt-4">
            Plan your photography shoots with precise golden hour and blue hour times.
            Know exactly when to be on location for that perfect natural light.
          </p>
        </div>
      </div>

      {/* Search Card */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-neutral-100">
        <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center gap-2">
          <FaMapMarkerAlt className="text-orange-500" />
          Choose Your Location
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              City / Place *
            </label>
            <input
              type="text"
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="e.g., Santorini, Kyoto"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              State / Region
            </label>
            <input
              type="text"
              value={searchState}
              onChange={(e) => setSearchState(e.target.value)}
              placeholder="e.g., Cyclades, Kansai"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Country
            </label>
            <input
              type="text"
              value={searchCountry}
              onChange={(e) => setSearchCountry(e.target.value)}
              placeholder="e.g., Greece, Japan"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Landmark (optional)
            </label>
            <input
              type="text"
              value={locationName}
              onChange={(e) => setLocationName(e.target.value)}
              placeholder="e.g., Oia Castle, Fushimi Inari"
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-2.5 bg-neutral-50 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
            />
          </div>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <FaSearch />
          )}
          {loading ? 'Calculating...' : 'Calculate Golden Hour'}
        </button>

        {error && (
          <p className="text-red-600 text-sm mt-3">{error}</p>
        )}
      </div>

      {/* Results */}
      {lightingData && (
        <div className="space-y-6 animate-fade-in">
          {/* Current Status Card */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-white/80 text-sm">Current Status</p>
                <h3 className="text-2xl font-bold">{lightingData.current_sun_status}</h3>
                <p className="text-white/90 mt-1">{lightingData.next_golden_hour}</p>
              </div>
              <div className="text-right">
                <p className="text-white/80 text-sm">{lightingData.location_name}</p>
                <p className="text-white/70 text-sm">{lightingData.date} ({lightingData.timezone})</p>
              </div>
            </div>
          </div>

          {/* Sun Times */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaSun className="text-xl text-amber-500" />
              </div>
              <p className="text-neutral-500 text-sm">Sunrise</p>
              <div className="text-2xl font-bold text-neutral-800">{lightingData.sunrise}</div>
              <p className="text-neutral-500 text-xs mt-1">Direction: {lightingData.sunrise_direction}</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaClock className="text-xl text-yellow-500" />
              </div>
              <p className="text-neutral-500 text-sm">Solar Noon</p>
              <div className="text-2xl font-bold text-neutral-800">{lightingData.solar_noon}</div>
              <p className="text-neutral-500 text-xs mt-1">Sun elevation: {lightingData.noon_sun_elevation}°</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FaMoon className="text-xl text-orange-500" />
              </div>
              <p className="text-neutral-500 text-sm">Sunset</p>
              <div className="text-2xl font-bold text-neutral-800">{lightingData.sunset}</div>
              <p className="text-neutral-500 text-xs mt-1">Direction: {lightingData.sunset_direction}</p>
            </div>
          </div>

          {/* Golden Hour Times */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-100">
            <h4 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
              <FaSun className="text-amber-500" />
              Golden Hour (Warm, Soft Light)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <span className="font-medium text-amber-800">Morning Golden Hour</span>
                </div>
                <div className="text-2xl font-bold text-amber-900">
                  {lightingData.morning_golden_hour_start} - {lightingData.morning_golden_hour_end}
                </div>
                <p className="text-amber-700 text-sm mt-1">Shortly after sunrise - warm, directional light</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-orange-400" />
                  <span className="font-medium text-amber-800">Evening Golden Hour</span>
                </div>
                <div className="text-2xl font-bold text-amber-900">
                  {lightingData.evening_golden_hour_start} - {lightingData.evening_golden_hour_end}
                </div>
                <p className="text-amber-700 text-sm mt-1">Hour before sunset - magical warm tones</p>
              </div>
            </div>
          </div>

          {/* Blue Hour Times */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
            <h4 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <FaMoon className="text-blue-500" />
              Blue Hour (Soft, Blue Light)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400" />
                  <span className="font-medium text-blue-800">Morning Blue Hour</span>
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {lightingData.morning_blue_hour_start} - {lightingData.morning_blue_hour_end}
                </div>
                <p className="text-blue-700 text-sm mt-1">Just before sunrise - cool, even light</p>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-3 h-3 rounded-full bg-indigo-400" />
                  <span className="font-medium text-blue-800">Evening Blue Hour</span>
                </div>
                <div className="text-2xl font-bold text-blue-900">
                  {lightingData.evening_blue_hour_start} - {lightingData.evening_blue_hour_end}
                </div>
                <p className="text-blue-700 text-sm mt-1">Just after sunset - dramatic blue tones</p>
              </div>
            </div>
          </div>

          {/* Photography Tips */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                <FaCamera className="text-purple-500" />
                Best For Portraits
              </h4>
              <p className="text-neutral-700">{lightingData.best_for_portraits}</p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
              <h4 className="font-semibold text-neutral-800 mb-3 flex items-center gap-2">
                <FaCompass className="text-emerald-500" />
                Best For Landscapes
              </h4>
              <p className="text-neutral-700">{lightingData.best_for_landscapes}</p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-purple-50 rounded-2xl p-5 border border-purple-100">
            <h4 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
              <FaInfoCircle className="text-purple-500" />
              Shooting Tips
            </h4>
            <ul className="space-y-2">
              {lightingData.recommendations.map((rec, idx) => (
                <li key={idx} className="text-purple-800 text-sm flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>

          {/* Day Length Info */}
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FaCalendarAlt className="text-xl text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500">Day Length</p>
                <p className="font-bold text-neutral-800">{lightingData.day_length}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-neutral-500">Coordinates</p>
              <p className="text-neutral-700">{lightingData.lat.toFixed(4)}°, {lightingData.lng.toFixed(4)}°</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Footer */}
      <div className="bg-amber-50 rounded-2xl p-6 flex items-start gap-4">
        <FaInfoCircle className="text-amber-500 text-xl flex-shrink-0 mt-1" />
        <div>
          <h4 className="font-semibold text-amber-900 mb-1">About Golden Hour</h4>
          <p className="text-sm text-amber-700">
            Golden hour is the period shortly after sunrise and before sunset when daylight is redder,
            softer, and more flattering. Blue hour occurs when the sun is below the horizon but still
            illuminating the sky with soft blue tones. Both are prized by photographers for natural portraits
            and landscapes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GoldenHourPlanner;
